import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';

import { TokenResponse } from 'src/app/shared/models/token-response.model';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../services/authentication.service';
import { BsCallApiService } from '../services/bs-call-api.service';
import { SessionService } from '../services/session.service';

@Injectable({
  providedIn: 'root'
})
export class BsRefreshTokenInterceptor implements HttpInterceptor {
  private isRefreshingToken = false;
  private tokenSubject: BehaviorSubject<TokenResponse> = new BehaviorSubject<TokenResponse>(null);

  constructor(
    private sessionService: SessionService,
    private authenticationService: AuthenticationService,
    private bsCallApiService: BsCallApiService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.applyRefreshToken(req, next);
  }

  private applyRefreshToken(req: HttpRequest<any>, next: HttpHandler) {
    if (this.checkIfUrlNeedsToken(req)) {
      if (this.sessionService.refreshToken && this.needsToRefreshToken()) {
        if (!this.isRefreshingToken) {
          this.isRefreshingToken = true;
          this.tokenSubject.next(null);
          return this.handleRefreshToken(req, next);
        }

        return this.tokenSubject.pipe(
          filter((token) => token != null),
          take(1),
          switchMap(() => {
            return next.handle(this.addToken(req, this.sessionService.accessToken));
          })
        );
      }

      return next.handle(req).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse) {
            if (error.status === 401) {
              return this.handleRefreshToken(req, next);
            }

            if (error.status === 410) {
              this.clearSessionAndRedirect();
              return observableThrowError(error);
            }

            if (error.status === 406) {
              this.clearSessionAndRedirectConcurrencyNotice();
              return observableThrowError(error);
            }

            return observableThrowError(error);
          }

          return observableThrowError(error);
        })
      );
    }

    return next.handle(req);
  }

  private checkIfUrlNeedsToken(event) {
    return (
      event.url.includes(environment.REFRESH_URL.DEVICE)
    );
  }

  private needsToRefreshToken() {
    const dateNow = Math.round(new Date().getTime() / 1000);
    const diffTime = dateNow - this.sessionService.dateToken;
    return diffTime > this.sessionService.expiresIn - 20;
  }

  private handleRefreshToken(req: HttpRequest<any>, next: HttpHandler) {
    return this.authenticationService.refreshToken().pipe(
      switchMap((tokenResponse: TokenResponse) => {
        const resultToken = tokenResponse.access_token;
        if (resultToken) {
          this.setValuesTokenResponse(tokenResponse);
          this.tokenSubject.next(tokenResponse);
          return next.handle(this.addToken(req, tokenResponse.access_token));
        }
        return;
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 400) {
          this.bsCallApiService.concurrencyCheck(this.sessionService.refreshToken).subscribe(
            (response: any) => {
              if (response.loggedOutByConcurrency === true) {
                this.clearSessionAndRedirectConcurrencyNotice();
              } else {
                this.clearSessionAndRedirect();
              }
            },
            () => {
              this.clearSessionAndRedirect();
            }
          );
          return;
        }
        return observableThrowError(error);
      }),
      finalize(() => {
        this.isRefreshingToken = false;
      })
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private clearSessionAndRedirect() {
    this.sessionService.clearSession();
    window.location.href = '/';
  }

  private clearSessionAndRedirectConcurrencyNotice() {
    this.sessionService.clearSession();
    window.location.href = '/removedByOtherUser';
  }

  private setValuesTokenResponse(tokenResponse: TokenResponse) {
    this.sessionService.accessToken = tokenResponse.access_token;
    this.sessionService.expiresIn = tokenResponse.expires_in;
    this.sessionService.refreshToken = tokenResponse.refresh_token;
    this.sessionService.tokenType = tokenResponse.token_type;
    this.sessionService.dateToken = this.getDateInSeconds();
  }

  private getDateInSeconds(): number {
    return Math.round(new Date().getTime() / 1000);
  }
}
